import { ChangeDetectionStrategy, Component, inject, Input, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EdependencyLevel } from '@df/catalog/model';
import { IProductDataV1, IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { RatioOptions } from '@df/ui/atomic/atomic-options';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import uniqBy from 'lodash-es/uniqBy';
import { FormMuiFieldComponent } from '../../common/form/mui-field/form-mui-field.component';
import { NTextBox, TextBoxComponent } from '../../common/text-box/text-box.component';
import { defaultImage } from '../../icms.config';

export namespace NAnalysisCalculator {
    export interface IComponentData extends NICmsContentComponent.IData, NTextBox.IData {
        image: string;
        ratio: string;
        productsIds: number[];
        reverbTimes: IReverbTime[];
    }

    export interface IReverbTime {
        label: string;
        value: number;
    }

    export interface IData {
        roomWidth?: number;
        roomHeight?: number;
        roomLength?: number;
        productId?: number;
        productOptionId?: number;
        reverbTime?: number;
    }

    export function getFields(noProductsField = false) {
        return [
            // Text
            ...NTextBox.getFields(false, true, false),
            // Image
            {
                key: 'image',
                type: NICmsContentField.EFieldType.image,
                title: 'Image',
                required: true,
                default: defaultImage
            },
            {
                key: 'ratio',
                type: NICmsContentField.EFieldType.choice,
                title: 'Ratio',
                options: RatioOptions,
                default: 'ratio-3-2',
                required: true
            },
            // Products
            noProductsField
                ? undefined
                : <NICmsContentField.IRelationField>{
                      key: 'productsIds',
                      type: NICmsContentField.EFieldType.relation,
                      title: 'Products',
                      options: {
                          title: 'Product',
                          type: 'product',
                          multiple: true
                      },
                      required: true,
                      default: [1]
                  },
            // Reverb Times
            <NICmsContentField.IRepeaterField>{
                key: 'reverbTimes',
                type: NICmsContentField.EFieldType.repeater,
                title: 'Reverb Times',
                default: [
                    { value: 0.5, label: 'Pro - Recording studios' },
                    { value: 1, label: 'Best - Classrooms and fine dining ' },
                    { value: 1.5, label: 'Better - Open offices and small theaters' },
                    { value: 2, label: 'Good - Worship spaces and multipurpose rooms' }
                ],
                required: true,
                itemName: 'Reverb Time',
                itemNameField: 'time',
                fields: [
                    <NICmsContentField.IField>{
                        key: 'label',
                        type: NICmsContentField.EFieldType.text,
                        title: 'Label',
                        required: true
                    },
                    <NICmsContentField.IField>{
                        key: 'value',
                        type: NICmsContentField.EFieldType.number,
                        title: 'Time',
                        required: true
                    }
                ]
            },
            NICmsContentComponent.backgroundField
        ].filter(i => !!i);
    }
}

@Component({
    imports: [TextBoxComponent, DfModuleImageModule, TranslatePipe, FormMuiFieldComponent, FormsModule, OptionLabelPipe],
    selector: 'analysis-calculator',
    templateUrl: './analysis-calculator.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalysisCalculatorComponent extends UiComponent implements NICmsContentComponent.IComponent, OnChanges {
    @Input()
    @ICmsContent({
        watchDepth: 1
    })
    component?: NAnalysisCalculator.IComponentData;

    @Input()
    productsIds?: number[];

    @Input()
    preselectFirst = false;

    private app = inject(App);

    data: NAnalysisCalculator.IData = {};

    products: Product<IProductDataV1>[] = [];

    productOptions: Product<IProductDataV3>[] | Product<IProductDataV2>[] = [];

    protected result = 0;

    override ngOnChanges(changes: any): void {
        if (changes.productsIds) {
            this.loadModels();
        }
    }

    async loadModels() {
        const ids = this.productsIds;
        if (ids?.length) {
            try {
                await this.app.resources.product.loadAllIds(ids, 1, { safe: true });
                this.products = this.app.resources.product.getAllExisting(ids);
                if (this.products.length) {
                    this.data.productId = this.products[0].id;
                    this.onProductChange();
                }
            } catch (e) {}
            this.detectChanges();
        }
    }

    calculateResult(): void {
        // Calculate the volume of the room
        const { roomHeight, roomWidth, roomLength, reverbTime } = this.data;
        if (roomHeight && roomHeight > 0 && roomWidth && roomWidth > 0 && roomLength && roomLength > 0 && reverbTime && this.nrc) {
            const roomVolume = 0.049 * roomWidth * roomLength * roomHeight;
            // Calculate the surface area of the room
            const surfaceArea = 2 * (roomWidth * roomLength) + 2 * (roomLength * roomHeight) + 2 * (roomHeight * roomWidth);
            // Apply the formula
            const result = (roomVolume / reverbTime - surfaceArea * 0.02) / (this.nrc - 0.02);
            this.result = Math.round(result);
        } else {
            this.result = 0;
        }
        this.detectChanges();
    }

    async onProductChange() {
        this.data.productOptionId = undefined;
        this.productOptions = [];
        this.calculateResult();
        this.detectChanges();
        try {
            if (this.currentProduct) {
                await this.currentProduct.load(3, { safe: true, dependencyLevel: EdependencyLevel.details });
            }
            if (this.currentProduct) {
                const productOptions = (
                    this.currentProduct?.isSimple ? [this.currentProduct] : this.currentProduct.children || []
                ) as Product<IProductDataV3>[];
                this.productOptions = uniqBy(
                    productOptions.filter(i => !!(i.data?.thickness && i.data?.nrc_calculator)),
                    product => product.data?.thickness + '' + product.data?.nrc_calculator
                );
            }
        } catch (e) {}
        this.detectChanges();
    }

    private get currentProduct(): Product<IProductDataV2> | undefined {
        const productId = this.data.productId;
        if (productId) {
            return this.app.resources.product.getExisting(productId);
        }
        return undefined;
    }

    private get productOptionProduct(): Product<IProductDataV3> | undefined {
        const productId = this.data.productOptionId;
        if (productId) {
            return this.app.resources.product.getExisting(productId) as Product<IProductDataV3>;
        }
        return undefined;
    }

    private get nrc(): number | undefined {
        return this.productOptionProduct?.data?.nrc_calculator;
    }
}
